/**
 * Estoque por filial (redesign admin, Fase 6). Puro e sem I/O.
 *
 * O contrato (`BranchStockSnapshot`) é servido por `/api/platform/stock/branches`
 * e hoje só existe como SEED DEMONSTRATIVO (`source: "demo_seed"`): o Bling
 * entrega um saldo único por produto. A tela rotula a origem em cada linha
 * para ninguém confundir demonstração com saldo operacional.
 *
 * Disponível = máximo(0, saldo − reservado), o mesmo cálculo de
 * `catalog/availability.ts`, aplicado por filial.
 */

export interface BranchRef {
  id: string;
  name: string;
}

export interface BranchStockItem {
  productId: string;
  sku: string;
  name: string;
  branchId: string;
  balance: number;
  reserved: number;
  updatedAt: string;
}

export interface BranchStockSnapshot {
  /**
   * `demo_seed` = demonstração; `platform` = saldo mantido pela equipe na Plataforma (C2, tabela
   * `branch_stock`); `erp` = saldo por filial vindo do ERP (se o Bling passar a publicar).
   */
  source: "demo_seed" | "platform" | "erp";
  branches: BranchRef[];
  items: BranchStockItem[];
}

export type BalanceFilter = "todos" | "sem-saldo" | "poucas" | "disponivel";

/** Corte de "poucas unidades": o mesmo do estoque crítico do catálogo (<= 3). */
export const LOW_STOCK_LIMIT = 3;

export function availableOf(item: Pick<BranchStockItem, "balance" | "reserved">): number {
  return Math.max(0, item.balance - item.reserved);
}

export type BranchSituation = "sem-saldo" | "poucas" | "disponivel";

export function situationOf(item: Pick<BranchStockItem, "balance" | "reserved">): BranchSituation {
  const available = availableOf(item);
  if (available === 0) return "sem-saldo";
  return available <= LOW_STOCK_LIMIT ? "poucas" : "disponivel";
}

function fold(text: string): string {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

export interface BranchStockFilters {
  /** `null` = a primeira filial da lista (a tela sempre mostra UMA unidade por vez). */
  branchId: string;
  query: string;
  balance: BalanceFilter;
}

export function filterBranchStock(snapshot: BranchStockSnapshot, filters: BranchStockFilters): BranchStockItem[] {
  const needle = fold(filters.query);
  return snapshot.items.filter((item) => {
    if (item.branchId !== filters.branchId) return false;
    if (needle && !fold(item.sku).includes(needle) && !fold(item.name).includes(needle)) return false;
    return filters.balance === "todos" || situationOf(item) === filters.balance;
  });
}

export function branchTotals(items: BranchStockItem[]): { balance: number; reserved: number; available: number } {
  return items.reduce(
    (sum, item) => ({
      balance: sum.balance + item.balance,
      reserved: sum.reserved + item.reserved,
      available: sum.available + availableOf(item)
    }),
    { balance: 0, reserved: 0, available: 0 }
  );
}

export function assertBranchStockSnapshot(value: unknown): BranchStockSnapshot {
  const fail = (reason: string): never => {
    throw new Error(`Invalid BranchStockSnapshot: ${reason}`);
  };
  if (typeof value !== "object" || value === null) return fail("payload is not an object");
  const record = value as Record<string, unknown>;
  if (record.source !== "demo_seed" && record.source !== "platform" && record.source !== "erp") return fail("unknown source");
  if (!Array.isArray(record.branches) || !Array.isArray(record.items)) return fail("branches and items must be arrays");
  for (const item of record.items as Array<Record<string, unknown>>) {
    if (typeof item.sku !== "string" || typeof item.branchId !== "string") return fail("item without sku/branchId");
    if (!Number.isInteger(item.balance) || !Number.isInteger(item.reserved)) return fail("balance/reserved must be integers");
  }
  return value as BranchStockSnapshot;
}
